import DocumentSource from './DocumentSource';
import Notification from '../core/Notification';
import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import PropTypes from 'prop-types';
import React from 'react';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import SecondaryButton from '../core/SecondaryButton';
import { globalStyles as styles } from '../core/style/gstheme';
import { BN_NAMESPACE } from '../core/utils/constants';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import BlankNodeHandler from './BlankNodeHandler';
import FindResourceDialog from './FindResourceDialog';
import { deleteBlankNode } from '../core/utils/utils';

class AnonymousPropertyHandler extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isErrored: undefined,
      constraintType: undefined
    };
    this.cancellable = null;
  }

  setConstraintType = (constraintType) => this.setState({ constraintType });

  exitFindResource = () => this.setState({ constraintType: undefined });

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  addInverse = (iri) => {
    GS.addProperty(this.props.id, this.props.graph, "inverseOf", iri, null, null, this.props.graph);
    this.setConstraintType(undefined);
  };

  deleteResource = () => deleteBlankNode(this.props.id, this.props.graph);

  render() {

    const { data, isReadOnly, id, graph, components, viewLang, defaultLang, projectIRI, sourceResource,
      context, sourceComponent, classes, top } = this.props;
    const { constraintType } = this.state;

    let label = undefined;
    let propName = undefined;

    if ("inverseOf" in data) {
      label = "Inverse Of";
      propName = "inverseOf";
    }

    const items = data.inverseOf;
    let excludeList = [sourceResource];
    if (items) {
      Array.isArray(items) ?
        excludeList = excludeList.concat(items)
        :
        excludeList.push(items);
    }

    if (constraintType) {
      return <FindResourceDialog
        key={viewLang}
        useButton={true}
        permitExternal={true}
        open={true}
        components={components}
        exclude={excludeList}
        defaultLang={defaultLang}
        permittedExternalType="ObjectProperty"
        sourceComponent={sourceComponent}
        searchClass={[
          "http://www.w3.org/2002/07/owl#ObjectProperty"
        ]}
        buttonTitle="Add a property"
        title="Add Property"
        project={projectIRI}
        graph={graph}
        viewLang={viewLang}
        onExitFindResource={this.exitFindResource}
        onAddResource={this.addInverse} />;
    }

    if (!items) return <div
      data-test="pe-outer"
      className={classes.incompleteData}
    >
      {isReadOnly &&
        <Notification text="This contains partially completed structures." dataTest="empty-partial" />
      }
      {!isReadOnly &&
        <div data-test="pe-selector">
          <Tooltip title='Delete expression'>
            <span style={{ float: 'right', marginBottom: 6 }}>
              <Typography variant="body2">
                <IconButton data-test="delete-expression" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                  <DeleteIcon />
                </IconButton>
              </Typography>
            </span>
          </Tooltip>
          <Typography variant="caption" className={classes.instruction}>Choose from</Typography>
          <span className={classes.optionsButton}>
            <SecondaryButton dataTest="inverse-of" onClick={() => this.setConstraintType("imverseOf")}>Inverse Of</SecondaryButton>
          </span>
        </div>
      }
    </div>;

    const itemsList = Array.isArray(items) ? items : [items];

    return (
      <div
        style={{ marginTop: 12 }}
        data-test="pe-outer"
        className={items.length === 0 ? classes.incompleteData : classes.completeData}
      >
        <div>
          <div style={{ display: 'flex', width: '100%' }}>
            <div>
              <Typography variant="caption" style={{ color: 'black' }} data-test="constraint-label">{label}</Typography>
            </div>
            {!isReadOnly &&
              <div style={{ flex: 1, textAlign: 'right' }}>
                <Tooltip title='Delete expression'>
                  <span style={{ float: 'right', marginBottom: 6 }}>
                    <Typography variant="body2">
                      <IconButton data-test="delete-expression" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                        <DeleteIcon />
                      </IconButton>
                    </Typography>
                  </span>
                </Tooltip>
                {items.length === 0 &&
                  <span style={{ marginLeft: 6 }}>
                    <FindResourceDialog
                      key={viewLang}
                      source={id}
                      permitExternal={true}
                      components={components}
                      exclude={excludeList}
                      defaultLang={defaultLang}
                      permittedExternalType="ObjectProperty"
                      sourceComponent={sourceComponent}
                      buttonTitle="Add property"
                      title="Add Property"
                      searchClass="http://www.w3.org/2002/07/owl#ObjectProperty"
                      project={projectIRI}
                      graph={graph}
                      viewLang={viewLang}
                      onAddResource={this.addNewResource} />
                  </span>
                }
              </div>
            }
          </div>
          {top &&
            <div style={{ width: 'calc(100% - 60px)', marginBottom: 12 }}>
              <DocumentSource
                graph={graph}
                componentIRI={sourceComponent}
                viewLang={viewLang}
                defaultLang={defaultLang}
                sources={data.documentSource} />
            </div>
          }
          <div data-test="pe-content" style={{ marginTop: 6 }}>
            {itemsList.map(item => item.startsWith(BN_NAMESPACE) ?
              <BlankNodeHandler
                key={item}
                id={item}
                handleNotAvailable={true}
                context={context}
                parent={data}
                sourceResource={sourceResource}
                sourceComponent={sourceComponent}
                graph={graph}
                projectIRI={projectIRI}
                components={components}
                isReadOnly={isReadOnly}
                viewLang={viewLang}
                defaultLang={defaultLang} />
              :
              <CommonIriDisplay
                id={item}
                key={item}
                components={components}
                projectIRI={projectIRI}
                handleNotAvailable={true}
                property={propName}
                isReadOnly={true}
                context={context}
                contextProperty="isDefinedBy"
                compact={true}
                graph={graph}
                source={id}
                wrap={false}
                viewLang={viewLang}
                defaultLang={defaultLang} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

AnonymousPropertyHandler.propTypes = {
  id: PropTypes.string,
  data: PropTypes.object,
  defaultLang: PropTypes.string,
  viewLang: PropTypes.string,
  top: PropTypes.bool,
  sourceResource: PropTypes.string.isRequired,
  sourceComponent: PropTypes.string.isRequired,
  components: PropTypes.array,
  context: PropTypes.string,
  graph: PropTypes.string,
  projectIRI: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool
};

export default withStyles(styles)(withGS(AnonymousPropertyHandler));